'use client';

import { type OTPInputProps as BaseProps, REGEXP_ONLY_DIGITS } from 'input-otp';
import { type ComponentProps, Fragment, useEffect, useRef } from 'react';

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  OTPCellPrimitive,
} from '../ui/input-otp';
import { useFieldControl } from './field';

export type OTPInputProps = Omit<
  BaseProps,
  'maxLength' | 'render' | 'children' | 'containerClassName' | 'onComplete'
> & {
  /** Number of cells. Default 6. */
  length?: number;
  /** Cells per group, split by the Figma Separator (3 + 3). 0 for one run. Default 3. */
  groupSize?: number;
  /** Called with the code once every cell is filled (typed or pasted) - submit here. */
  onComplete?: (code: string) => void;
  /** Class for the row of cells. */
  className?: string;
};

/** Pasted codes often arrive as "482 916" or "482-916". */
const stripSeparators = (text: string) => text.replace(/[\s-]/g, '');

/**
 * Six-cell one-time-code entry, 3 + 3 with a separator for chunking (Figma:
 * Components ↳ OTP ↳ OTP Input). One real input (input-otp) under drawn
 * cells: typing auto-advances, Backspace moves back, arrows move between
 * cells, and pasting a full code fills every cell and calls `onComplete` -
 * keep a Verify button for assistive tech too. `inputMode="numeric"`,
 * `autoComplete="one-time-code"` and digits only by default (`pattern`).
 * Error (`aria-invalid`, or a Field `error`): every cell turns danger, the
 * message goes below (Field) and focus returns to the first cell.
 */
export function OTPInput({
  length = 6,
  groupSize = 3,
  className,
  pattern = REGEXP_ONLY_DIGITS,
  pasteTransformer = stripSeparators,
  inputMode = 'numeric',
  autoComplete = 'one-time-code',
  onComplete,
  ...props
}: OTPInputProps) {
  const inputProps = useFieldControl(props) as typeof props & {
    'aria-invalid'?: boolean | 'true' | 'false';
  };
  const invalid =
    inputProps['aria-invalid'] === true || inputProps['aria-invalid'] === 'true';
  const disabled = Boolean(inputProps.disabled);
  const ref = useRef<HTMLInputElement>(null);

  // When an error comes in, focus returns to the first cell so the code can
  // be retyped. Not on mount: a page rendered with an error keeps its focus.
  const wasInvalid = useRef(invalid);
  useEffect(() => {
    const input = ref.current;
    const becameInvalid = invalid && !wasInvalid.current;
    wasInvalid.current = invalid;
    if (!becameInvalid || !input || input.disabled) return;
    input.focus();
    input.setSelectionRange(0, Math.min(1, input.value.length));
  }, [invalid]);

  const size = groupSize > 0 ? groupSize : length;
  const groups = Array.from({ length: Math.ceil(length / size) }, (_, g) =>
    Array.from({ length: Math.min(size, length - g * size) }, (_, i) => g * size + i),
  );

  return (
    <InputOTP
      ref={ref}
      maxLength={length}
      pattern={pattern}
      pasteTransformer={pasteTransformer}
      inputMode={inputMode}
      autoComplete={autoComplete}
      onComplete={onComplete}
      containerClassName={className}
      {...inputProps}
    >
      {groups.map((cells, g) => (
        <Fragment key={g}>
          {g > 0 && <InputOTPSeparator />}
          <InputOTPGroup>
            {cells.map((index) => (
              <InputOTPSlot
                key={index}
                index={index}
                invalid={invalid}
                disabled={disabled}
              />
            ))}
          </InputOTPGroup>
        </Fragment>
      ))}
    </InputOTP>
  );
}

export type OTPCellProps = ComponentProps<typeof OTPCellPrimitive>;

/**
 * One character of a one-time code, drawn from explicit state (Figma:
 * Components ↳ OTP ↳ OTP Cell): `char`, `active` (Focus: halo, plus `caret`
 * when empty), `invalid`, `disabled`. OTPInput draws its cells with it; use
 * it directly only for static renders (previews, docs).
 */
export function OTPCell(props: OTPCellProps) {
  return <OTPCellPrimitive aria-hidden {...props} />;
}
